import React, { useEffect, useState } from 'react';
import { ConnectedWallet, usePrivy, useWallets } from '@privy-io/react-auth';
import { Web3ProviderContext } from './context';
import { Chain, Address } from 'viem';
import { useAccount, useSwitchChain, type UseAccountReturnType, useWalletClient, usePublicClient } from 'wagmi';
import { disconnect } from '@wagmi/core';
import { useEEAToken } from './useEEAToken';
import { ethers } from 'ethers';
import { SUPPORTED_CHAINS, wagmiConfig, ENVIO_GRAPHQL_ENDPOINT, ENVIO_API_KEY } from '../../config';
import { SwitchChain } from '../../components/SwitchChain';
import { getConnections, switchAccount } from '@wagmi/core';
import { EnvioGraphQLClient, type EnvioGraphQLClientConfig } from '@peeramid-labs/sdk';
import { useRouter } from 'next/router';

export const Web3Provider = ({ children }: { children: React.ReactNode }) => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [hadWagmiChainChanged, setHadWagmiChainChanged] = useState<boolean>(false);
  const [privyWalletChainId, setPrivyWalletChainId] = useState<number | null>(null);
  const [currentChain, setCurrentChain] = useState<Chain>();
  const [account, setAccount] = useState<Address | null>(null);
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  const { logout: privyLogout, authenticated, user } = usePrivy();
  const [web3Provider, setWeb3Provider] = useState<ethers.providers.Web3Provider | null>(null);
  const { wallets }: { wallets: ConnectedWallet[] } = useWallets();
  const eeaToken = useEEAToken({ provider: web3Provider, account: account || '', chainId: currentChain?.id });

  const { switchChain } = useSwitchChain();
  const wagmiAccount: UseAccountReturnType = useAccount();
  const router = useRouter();
  useEffect(() => {
    (async () => {
      try {
        if (authenticated && user && wallets.length > 0) {
          const embedWallet = wallets.find((wallet) => wallet.walletClientType === 'privy');
          const wallet: ConnectedWallet = embedWallet ?? wallets[0];
          const privyWalletChainId = parseInt(wallet.chainId.split(':')[1]);
          setPrivyWalletChainId(privyWalletChainId);
          const ethereumProvider = await wallet.getEthereumProvider();
          const web3Provider = new ethers.providers.Web3Provider(ethereumProvider);
          setWeb3Provider(web3Provider);
          const activeSupportedChain = SUPPORTED_CHAINS.find((chain) => chain.id === privyWalletChainId);
          if (activeSupportedChain !== undefined) {
            if (wagmiAccount.status === 'connected') {
              if (wagmiAccount.address !== wallet.address) {
                //if privy and wagmi accounts are not in sync
                const connections = getConnections(wagmiConfig);
                const connectionWithPrivyEmbeddedWallet = connections.find((connection) =>
                  connection.accounts.includes(wallet.address as Address)
                );
                if (connectionWithPrivyEmbeddedWallet) {
                  await switchAccount(wagmiConfig, {
                    connector: connectionWithPrivyEmbeddedWallet.connector,
                  });
                  return;
                } else {
                  console.error('No connection found for the embedded wallet');
                  window.location.reload();
                  return;
                }
              } else {
                setAccount(wagmiAccount.address);
                setCurrentChain(activeSupportedChain);
                eeaToken?.ensureEEAToken();
                setIsConnected(true);
                console.log('connected to chain', activeSupportedChain);
              }
            } else {
              if (!hadWagmiChainChanged) {
                setHadWagmiChainChanged(true);
                await switchChain({ chainId: privyWalletChainId });
              }
            }
          }
        } else {
          setWeb3Provider(null);
          setAccount(null);
          setCurrentChain(undefined);
          setIsConnected(false);
          setHadWagmiChainChanged(false);
          setPrivyWalletChainId(null);
        }
      } catch (error) {
        console.error('Error during login:', error);
      }
    })();
  }, [wallets, wagmiAccount, authenticated, user, switchChain, hadWagmiChainChanged]);

  const logout = async () => {
    try {
      const wallet: ConnectedWallet = wallets[0];
      if (wallet) {
        await wallet.disconnect();
      }
      if (window.ethereum) {
        window.ethereum = null;
      }
      if (typeof sessionStorage.getItem('EEAToken') != 'undefined') {
        sessionStorage.removeItem('EEAToken');
      }
      Object.keys(localStorage).forEach((key) => {
        if (key === 'EEAToken' || key.startsWith('EEAToken')) {
          localStorage.removeItem(key);
        }
      });
      if (typeof sessionStorage.getItem('incomingPath') != 'undefined') {
        sessionStorage.removeItem('incomingPath');
      }
      await disconnect(wagmiConfig);
      await privyLogout();
      setIsConnected(false);
      setAccount(null);
      setCurrentChain(undefined);
      setHadWagmiChainChanged(false);
      setPrivyWalletChainId(null);

    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  return (
    <Web3ProviderContext.Provider
      value={{
        currentChain,
        account,
        isConnected,
        logout,
        ensureEEAToken: eeaToken?.ensureEEAToken,
        publicClient,
        walletClient,
        envioClient: new EnvioGraphQLClient({
          endpoint: ENVIO_GRAPHQL_ENDPOINT,
          apiKey: ENVIO_API_KEY,
        } as EnvioGraphQLClientConfig),
      }}
    >
      {SUPPORTED_CHAINS.find((chain) => chain.id === privyWalletChainId) === undefined &&
      privyWalletChainId !== null ? (
        <SwitchChain />
      ) : (
        children
      )}
    </Web3ProviderContext.Provider>
  );
};

export default Web3Provider;
